import { S25Util } from "../util/s25-util";
import { jSith } from "../util/jquery-replacement";

/***************************************************************************************************************************************
To access _repository and _datetime repository use window.angBridge.CacheRepository to be sure we access the global cache at all times.
If using just CacheRepository, we run into issues similar to those reported in ANG-3647.
For TS components injected into JS via <s25-angular-wrapper> any/all cached service methods are stored in a seperate cache.
I'm not sure why, it is almost like a new instance of CacheRepository is created for TS components. Which should not be the case.
Additionally this is only a prod with compiled code (prod-all) dev code works great either way.
-travis
*****************************************************************************************************************************************/
class CacheOptions {
    public immutable?: boolean;
    public cachedMethod?: string;
    public expireTimeMs?: number;
    public ignoreArgsInKey?: boolean;
    public hitNotify?: boolean;
    public targetName: string;
}

type InvalidateOptions = InvalidateOptions.Base | InvalidateOptions.ByObjectFunc | InvalidateOptions.ByPattern;
namespace InvalidateOptions {
    export type Base = {
        validate?: (args: any[]) => boolean;
        serviceName?: string;
        methodName?: string;
    };

    export type ByPattern = {
        patternFunc: (args: any[]) => string;
        startsWith?: string;
    } & Base;

    export type ByObjectFunc = {
        objectFunc: (args: any[]) => any[];
    } & Required<Pick<Base, "serviceName" | "methodName">>;
}

export class CacheRepository {
    public static _repository: any = Object.assign({}, (window.ProData && window.ProData.initCache) || {});

    public static _datetimeRepository: any = {};

    public static get(key?: string) {
        if (typeof key === "undefined") {
            return undefined;
        }
        return window.angBridge.CacheRepository._repository[key];
    }

    public static getLastRunDateTime(key?: string): Date | undefined {
        if (typeof key === "undefined") {
            return undefined;
        }
        return window.angBridge.CacheRepository._datetimeRepository[key];
    }

    public static put(key: string | undefined, value: any) {
        if (typeof key === "undefined") {
            return;
        }
        window.angBridge.CacheRepository._repository[key] = value;
        window.angBridge.CacheRepository._datetimeRepository[key] = new Date();
    }

    public static invalidate(key?: string) {
        if (typeof key === "undefined") {
            return;
        }
        window.angBridge.CacheRepository._repository[key] = undefined;
        window.angBridge.CacheRepository._datetimeRepository[key] = undefined;
    }

    public static invalidateFunction(func: (...args: unknown[]) => unknown) {
        if (Cache.isCachedFunction(func)) {
            CacheRepository.invalidateByService(`${func.service}.${func.method}`);
        } else {
            throw new Error("Expected a service and method name to invalidate function cache.");
        }
    }

    public static invalidateByStartsWith(startsWith: string, stringPattern?: string) {
        const regExPattern = stringPattern && new RegExp(stringPattern, "gi");
        if (typeof startsWith === "undefined") {
            return;
        }
        const startsWithDelimited = startsWith + "."; //key parts are delimited by "."
        jSith.forEach(window.angBridge.CacheRepository._repository, function (key: string) {
            if (
                (key === startsWith || key.indexOf(startsWithDelimited) === 0) &&
                (!regExPattern || key.search(regExPattern) > -1)
            ) {
                CacheRepository.invalidate(key);
            }
        });
    }

    public static invalidateByObject(service: string, method: string, argsArr: any[]) {
        argsArr = S25Util.deepCopy(argsArr);
        const key = service + "." + method + "." + S25Util.toJson(CacheRepository.valuesAsString(argsArr)); //args must be an array
        CacheRepository.invalidate(key);
    }

    public static invalidateByObjectPattern(serviceMethod: string, regExPattern?: any) {
        //note: serviceMethod includes serviceName, such as: "PreferenceService.getPreferences"
        CacheRepository.invalidateByStartsWith(serviceMethod, regExPattern);
    }

    public static invalidateByMethod(methodName: string, stringPattern?: string) {
        //note: serviceMethod includes serviceName, such as: "PreferenceService.getPreferences"
        CacheRepository.invalidateByStartsWith(methodName, stringPattern);
    }

    public static invalidateByService(serviceName: string, stringPattern?: string) {
        CacheRepository.invalidateByStartsWith(serviceName, stringPattern);
    }

    public static invalidateAll() {
        window.angBridge.CacheRepository._repository = {};
        window.angBridge.CacheRepository._datetimeRepository = {};
    }

    public static valuesAsString(json: any, cache?: any[]) {
        cache = cache || [];
        if (json) {
            for (const key in json) {
                if (json.hasOwnProperty(key)) {
                    if (S25Util.isObject(json[key]) && cache.indexOf(json[key]) > -1) {
                        continue;
                    } //circular reference
                    cache.push(json[key]);
                    if (S25Util.array.isArray(json[key])) {
                        for (let i = 0; i < json[key].length; i++) {
                            CacheRepository.valuesAsString(json[key][i], cache);
                        }
                    } else if (S25Util.isObject(json[key])) {
                        CacheRepository.valuesAsString(json[key], cache);
                    } else if (!S25Util.isString(json[key]) && S25Util.isNumeric(json[key])) {
                        json[key] = "" + json[key];
                    }
                }
            }
        }
        return json;
    }

    public static composeKey(service: string, method: string, args?: any[], isCacheKeyIgnoreArgs?: boolean) {
        let key = service + "." + method;
        if (!isCacheKeyIgnoreArgs && args && args.length > 0) {
            //note: cannot cache if any argument itself is a function (i.e. callback)
            for (let i = 0; i < args.length; ++i) {
                if (typeof args[i] === "function") {
                    return undefined;
                }
            }

            //convert args to an array so we can invalidate by key easily (ie, key is just an array)
            //then convert any numeric array entries (arguments) to strings so that we can hit and invalidate the cache
            //without regard to argument type. Eg, serviceCall(1) === serviceCall("1") for a cache hit and invalidate(serviceCall("1"))
            //invalidates serviceCall("1") *and* serviceCall(1)
            key += "." + S25Util.toJson(CacheRepository.valuesAsString(S25Util.deepCopy(args)));
        }
        return key;
    }

    public static handlePromise(promise: any, isDataImmutable?: boolean, hitNotify?: boolean, hitOrMiss?: string) {
        if (promise && promise.forcePromise) {
            return promise.forcePromise();
        }

        if (!isDataImmutable) {
            if (hitNotify) {
                return promise.then(function (data: any) {
                    data.cacheHit = hitOrMiss === "hit";
                    return data;
                });
            } else {
                return promise;
            }
        }

        if (typeof promise.then !== "function") {
            return promise;
        }

        //copy to prevent writes to cache
        return promise.then(function (data: any) {
            if (hitNotify) {
                return Object.assign(S25Util.deepCopy(data), { cacheHit: hitOrMiss === "hit" });
            }
            return S25Util.deepCopy(data);
        });
    }

    public static aspect(
        func: any,
        service: string,
        method: string,
        cachedMethod?: string,
        expireTimeMs?: number,
        isDataImmutable?: boolean,
        isCacheKeyIgnoreArgs?: boolean,
        hitNotify?: boolean,
    ): Cache.Func {
        let aspectFunc: (...args: any[]) => any;
        // forcedMethod - always run original method, get results, and cache results under specified separate [cachedMethod]
        if (cachedMethod) {
            aspectFunc = function (...args: any[]) {
                // note: include our arguments into the key -i.e. assume (cachedMethod) has the same arguments
                const key = CacheRepository.composeKey(service, cachedMethod, args, isCacheKeyIgnoreArgs);

                // note - invalidate cache, so srcMethod running might call cachedMethod and it will have cache miss and will refresh data
                CacheRepository.invalidate(key);

                // run srcMethod
                const promise = func.apply(this, args);

                // note: cache our promise under cacheMethod key in case our method did not call cachedMethod but called DAO directly (legacy)
                // (even though cacheMethod might already been called and might already have cached the promise)
                CacheRepository.put(key, promise);
                if (promise?.catch && key) {
                    promise.catch(() => {
                        if (CacheRepository.get(key) === promise) CacheRepository.invalidate(key);
                    });
                }
                return CacheRepository.handlePromise(promise, isDataImmutable, hitNotify, "miss");
            };
        } else {
            // no cachedMethod - normal cache on [service, method]
            // We join all calls to the service to a single call by caching singleton instance of its returned promise.
            // The callers will run .then() method on the cached promise which is the same singleton for all callers.
            // Thus callers will wait till the singleton is resolved.
            aspectFunc = function (...args: any[]) {
                // include the arguments of the method (if any); note - arguments are intercepted at runtime
                const key = CacheRepository.composeKey(service, method, args, isCacheKeyIgnoreArgs);

                const cachedPromise = CacheRepository.get(key);

                // hit
                if (cachedPromise) {
                    // no expireTimeMs specified
                    if (!expireTimeMs) {
                        return CacheRepository.handlePromise(cachedPromise, isDataImmutable, hitNotify, "hit");
                    }

                    // check lastRunTime
                    const lastRunTime = CacheRepository.getLastRunDateTime(key);

                    // if we have expire time and last run time, and are within expire time
                    if (expireTimeMs && lastRunTime && new Date().getTime() - lastRunTime.getTime() < expireTimeMs) {
                        return CacheRepository.handlePromise(cachedPromise, isDataImmutable, hitNotify, "hit");
                    }

                    // expireTimeMs specified, but lastRunTime is older - cannot use cachedPromise, it is cash miss
                    // do nothing - miss hanlded below
                }

                // miss
                const promise = func.apply(this, args);
                // cache the promise returned by original service
                CacheRepository.put(key, promise);
                if (promise?.catch && key) {
                    promise.catch(() => {
                        if (CacheRepository.get(key) === promise) CacheRepository.invalidate(key);
                    });
                }

                return CacheRepository.handlePromise(promise, isDataImmutable, hitNotify, "miss");
            };
        }
        return Cache.copyMeta({
            into: aspectFunc,
            src: {
                service,
                method,
            },
        });
    }

    public static getCachedMethod(func: any, service: string, method: string, options: CacheOptions) {
        return CacheRepository.aspect(
            func,
            service,
            method,
            options.cachedMethod,
            options.expireTimeMs,
            options.immutable,
            options.ignoreArgsInKey,
            options.hitNotify,
        );
    }

    public static invalidateAspect(func: any, options: InvalidateOptions) {
        return function (...args: any[]) {
            //if validation provided, skip cache invalidation if validate function returns falsy
            if ("validate" in options && options.validate) {
                if (!options.validate(args)) {
                    return func.apply(this, args); //call original delegate
                }
            }

            let stringPattern = undefined;
            if ("patternFunc" in options && options.patternFunc) {
                stringPattern = options.patternFunc(args); //extract pattern from args
            }

            if ("objectFunc" in options) {
                const obj: any = options.objectFunc(args); //extract object from args
                CacheRepository.invalidateByObject(options.serviceName, options.methodName, obj);
            } else if ("startsWith" in options && options.startsWith) {
                CacheRepository.invalidateByStartsWith(options.startsWith, stringPattern);
            } else if (options.serviceName) {
                let serviceName = options.serviceName;
                if (options.methodName) {
                    //if method name give, append it to service name
                    serviceName += "." + options.methodName;
                }
                CacheRepository.invalidateByService(serviceName, stringPattern);
            } else if (options.methodName) {
                CacheRepository.invalidateByMethod(options.methodName, stringPattern);
            }
            return func.apply(this, args); //call original delegate
        };
    }

    /*
    Provides a shared cache for services that can return optional data.
    For example if you have one call that fetches optional data A and B, and then make another call that fetches
    optional data B and C, this shared cache would remember that B was fetched in the first call, and only fetch C
    in the second call.
     */
    public static sharedCache<Arg, Data>(
        serviceName: string,
        methodName: string,
        args: Arg[],
        service: (args: Arg[]) => Promise<Data>,
        options: {
            reducer?: (accumulator: Data, currentValue: Data) => Data;
            initialValue?: Data; // If not provided, the first value will be used
            additionalKeys?: unknown[];
            expireTimeMs?: number;
            immutable?: boolean;
        },
    ) {
        let { initialValue, expireTimeMs, immutable } = options;
        expireTimeMs ??= 0;
        const defaultReducer = Object.assign as (accumulator: Data, currentValue: Data) => Data;
        const reducer = options.reducer ?? defaultReducer;
        const additionalKeys = options.additionalKeys || [];

        // Check cache
        const key = CacheRepository.composeKey(serviceName, methodName, additionalKeys);
        const cached = CacheRepository.get(key) as SharedCache<Arg, Data>;
        const now = Date.now();
        const cachedArgs = new Set(
            (cached?.arguments ?? []).filter((arg) => !expireTimeMs || arg.exp >= now).map((arg) => arg.argument),
        );
        const uncachedArgs = args.filter((arg) => !cachedArgs.has(arg));
        let dataPromise = cached?.data;

        // Fetch uncached
        if (!!uncachedArgs.length) {
            const serviceData = service(uncachedArgs);

            const reducedPromise = Promise.all([cached?.data, serviceData]).then(([cachedData, newData]) => {
                if (initialValue && !cached) return reducer(initialValue, newData);
                if (!cached) return newData;
                return reducer(cachedData, newData);
            });

            const newCached: SharedCache<Arg, Data> = {
                arguments: (cached?.arguments ?? []).concat(
                    uncachedArgs.map((arg) => ({ argument: arg, exp: now + expireTimeMs })),
                ),
                data: reducedPromise,
            };

            CacheRepository.put(key, newCached);

            dataPromise = reducedPromise;
        }

        if (immutable) return dataPromise.then(S25Util.deepCopy);
        return dataPromise;
    }
}

export function Cache(options = new CacheOptions()): MethodDecorator {
    return (target: any, propertyKey: string, descriptor: PropertyDescriptor) => {
        const service = options.targetName || target.name;
        const method = propertyKey;
        if ("value" in descriptor) {
            descriptor.value = CacheRepository.getCachedMethod(descriptor.value, service, method, options);
        } else if ("get" in descriptor) {
            descriptor.get = CacheRepository.getCachedMethod(descriptor.get, service, method, options);
        } else {
            throw new Error("Cache decorator can only be applied to methods or getters.");
        }
        return descriptor;
    };
}

export namespace Cache {
    export type Meta = {
        service: string;
        method: string;
    };
    export type Func = ((...args: unknown[]) => unknown) & Meta;

    export function isCachedFunction(obj: unknown): obj is Func {
        return typeof obj === "function" && hasMeta(obj);
    }

    export function hasMeta(obj: any): obj is Meta {
        return obj && typeof obj.service === "string" && typeof obj.method === "string";
    }

    export function copyMeta<T = (...args: any[]) => any>({ into, src }: { into: T; src: Meta }): T & Meta;
    export function copyMeta<T = (...args: any[]) => any>({ into, src }: { into: T; src: unknown }): T {
        if (hasMeta(src)) {
            (<Func>into).service = src.service;
            (<Func>into).method = src.method;
        }
        return into;
    }
}

export function Invalidate(options: InvalidateOptions = {}): MethodDecorator {
    return (target: any, propertyKey: string, descriptor: PropertyDescriptor) => {
        if ("value" in descriptor) {
            const func = descriptor.value;
            descriptor.value = CacheRepository.invalidateAspect(func, options);
        } else if ("get" in descriptor) {
            const func = descriptor.get;
            descriptor.get = CacheRepository.invalidateAspect(func, options);
        }
        return descriptor;
    };
}

export type SharedCache<Arg extends unknown, Data> = {
    arguments: { argument: Arg; exp: EpochTimeStamp }[];
    data: Promise<Data>;
};
